import { useCallback } from 'react';

import { useNovelRepository } from '@/hooks/useNovelRepository';
import type { EntitySummary } from '@/lib/types';

import { useEntityAccessLayer } from '../../shared/hooks/use-entity-access-layer';
import { mapOrganizationDTOToDomain } from '../api/mappers';
import type { OrganizationFormValues } from '../schemas';
import type { Organization } from '../types';

/**
 * Fields on the Organization that are mirrored into the EntitySummary.
 * When any of these change, the summary is automatically updated.
 */
const ORGANIZATION_SUMMARY_FIELDS = [
  'name',
  'type',
  'shortDescription',
  'mainImageUrl',
  'mainImagePath',
] as const;

/**
 * Centralised access layer for organizations.
 *
 * @example
 * ```tsx
 * const {
 *   summaries: organizations,
 *   organization,
 *   sections,
 *   updateItem: updateOrganization,
 *   deleteItem: deleteOrganization,
 *   isLoading,
 * } = useOrganizationAccessLayer(organizationId);
 * ```
 */
export function useOrganizationAccessLayer(organizationId?: string) {
  const {
    summaries,
    item,
    sections,
    isLoading,
    error,
    updateItem,
    deleteItem,
    getNewId,
  } = useEntityAccessLayer<Organization, EntitySummary>({
    entityType: 'organizations',
    summaryFieldKeys: ORGANIZATION_SUMMARY_FIELDS,
    itemId: organizationId,
    itemMapper: mapOrganizationDTOToDomain,
  });

  const {
    saveItem,
    updateEntitySummary,
  } = useNovelRepository();

  /**
   * Creates a new organization with initial data + summary.
   * NOTE: Image upload is NOT handled here — call this AFTER
   * images have been processed.
   */
  const createOrganization = useCallback(
    async (id: string, data: Omit<OrganizationFormValues, 'pendingFiles'>) => {
      const now = new Date().toISOString();
      const newOrg = {
        ...data,
        id,
        audit: { createdAt: now, updatedAt: now },
      };

      // Persist item
      await saveItem('entities', 'organizations', newOrg);

      // Extract summary fields with safe string coercion
      const name = String(data.name ?? '');
      const type = String(data.type ?? '');
      const shortDescription = String(data.shortDescription ?? '');
      const mainImageUrl = String(data.mainImageUrl ?? '');
      const mainImagePath = String((data as Record<string, unknown>).mainImagePath ?? '');
      const primaryImageId = typeof data.primaryImageId === 'string' ? data.primaryImageId : null;

      // Write summary
      await updateEntitySummary('organizations', id, {
        id,
        name,
        type,
        shortDescription,
        mainImageUrl,
        mainImagePath,
        primaryImageId,
        updatedAt: now,
      });
    },
    [saveItem, updateEntitySummary],
  );

  return {
    /** Organization summary list, sorted by updatedAt DESC. */
    organizations: summaries,
    /** Single organization domain model (if ID provided). */
    organization: item,
    /** Organization UI settings (sections/fields). */
    sections,
    isLoading,
    error,

    /** Partial-update an organization. Auto-syncs summary if needed. */
    updateOrganization: updateItem,

    /** Delete an organization and its summary. */
    deleteOrganization: deleteItem,

    /** Create a new organization (after image processing). */
    createOrganization,

    /** Generate a Firestore-compatible ID for a new organization. */
    getNewId,
  };
}
